/**
 * She-Hulk — „Marvel: Brains and Brawn".
 *
 * JUST THROW SOMETHING: na začiatku ťahu smie odhodiť kartu a spôsobiť
 * poškodenie za jej BOOST figúrke vo svojej zóne. Odhodená karta leží po
 * odhodení navrchu odhodu — pokračovanie `special` si ju tam nájde.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  discardRandom,
  discardToDeck,
  drawCard,
  emit,
  gainAction,
  getMap,
  getPlayer,
  healFighter,
  millDeck,
  shuffleDeck,
} from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex } from '../types.js';
import { feint } from './common.js';
import { fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const HULK = 'she-hulk';
const LEGALESE = 'she_hulk.legalese';
const SAVAGE = 'she_hulk.the-savage-she-hulk';
const SENSATIONAL = 'she_hulk.sensational';
const LEAP = 'she_hulk.leap-toward';

/** Počet akcií v ťahu — `ACTIONS_PER_TURN` z game.ts sa sem importovať nedá (kruh). */
const ACTIONS_PER_TURN = 2;

const hulkOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === HULK) ?? null;

/** Figúrky v zóne She-Hulk okrem nej samej. */
function inHerZone(ctx: Ctx, player: PlayerIndex): string[] {
  const hulk = hulkOf(ctx, player);
  if (!hulk) return [];
  const map = getMap(ctx);
  return ctx.state.fighters.filter((f) => f.id !== hulk.id && shareZone(map, f.spaceId, hulk.spaceId)).map((f) => f.id);
}

/**
 * „DURING: Discard the top card of your opponent's deck. Increase the value of
 * this card by twice that card's boost value." Mení stav, preto `duringChoice`.
 */
const ladyJustice: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const opponent = opponentOf(e);
    if (!combat || opponent === null) return;
    const top = getPlayer(e.ctx.state, opponent).deck[0];
    if (!top) return;
    millDeck(e.ctx, opponent, 1);
    const side = e.role === 'defender' ? 'defender' : 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += 2 * cardDef(e.ctx, top).boost;
  },
};

/** „Draw cards equal to the amount of combat damage you took." */
const theDefenseRests: CardEffect = {
  after: (e) => {
    for (let i = 0; i < (e.damage ?? 0); i++) drawCard(e.ctx, e.player);
  },
};

/** „IMMEDIATELY: Cancel all effects on your opponent's card. DURING: If you are attacking, the value of this card is 4 instead." */
const ceaseAndDesist: CardEffect = {
  cancelsOpponent: true,
  during: (e, value) => (e.role === 'attacker' ? 4 : value),
};

/**
 * „If you won the combat, your opponent chooses whether you both draw a card
 * or both discard a card. If you lost the combat, you make the choice instead."
 */
const legalese: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const both = `${e.player}:${opponent}`;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.won ? opponent : e.player,
      source: e.cardId,
      label: 'Legalese: obaja si potiahnete kartu, alebo obaja jednu odhodíte?',
      optional: false,
      options: [
        { label: 'Obaja ťaháte kartu', follow: { kind: 'effect', effect: LEGALESE, data: `draw:${both}` } },
        { label: 'Obaja odhadzujete kartu', follow: { kind: 'effect', effect: LEGALESE, data: `discard:${both}` } },
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    const [what, ...players] = data.split(':');
    for (const player of players.map(Number)) {
      if (what === 'draw') {
        drawCard(ctx, player);
      } else if (getPlayer(ctx.state, player).hand.length > 0) {
        askChoice(ctx, {
          kind: 'card',
          player,
          source: choice.source,
          label: 'Legalese: odhoď 1 kartu',
          optional: false,
          from: 'hand',
          purpose: 'discard',
          discards: true,
        });
      }
    }
  },
};

/** „IMMEDIATELY: If She-Hulk is adjacent to the opposing fighter, the opposing fighter may not leave their space this turn." */
const nerveClusterStrike: CardEffect = {
  immediate: (e) => {
    const hulk = hulkOf(e.ctx, e.player);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!hulk || !them || !neighbors(getMap(e.ctx), hulk.spaceId).includes(them.spaceId)) return;
    (e.ctx.state.turn.pinned ??= []).push(them.id);
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: HULK, name: `Nerve Cluster Strike: ${them.name} tento ťah neopustí svoj priestor` });
  },
};

/** „DURING: You may spend an additional action to make this card's value 9 instead." */
const savageSheHulk: CardEffect = {
  duringChoice: (e) => {
    const turn = e.ctx.state.turn;
    if (e.role !== 'attacker' || turn.player !== e.player) return;
    if (turn.actionsUsed.length >= ACTIONS_PER_TURN + (turn.extraActions ?? 0)) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'The Savage She-Hulk: minúť ďalšiu akciu a útočiť za 9?',
      optional: true,
      effect: SAVAGE,
      yes: 'Minúť akciu — útok 9',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const turn = ctx.state.turn;
    const combat = ctx.state.combat;
    if (!combat || turn.actionsUsed.length >= ACTIONS_PER_TURN + (turn.extraActions ?? 0)) return;
    turn.actionsUsed.push('attack');
    emit(ctx, { type: 'actionUsed', player: choice.player, kind: 'attack' });
    (combat.flags ??= []).push('savage');
  },
  during: (e, value) => (e.ctx.state.combat?.flags?.includes('savage') ? 9 : value),
};

/** „If you won the combat, deal 1 damage to each fighter adjacent to She-Hulk." */
const greenEnergy: CardEffect = {
  after: (e) => {
    const hulk = hulkOf(e.ctx, e.player);
    if (!e.won || !hulk) return;
    const around = new Set(neighbors(getMap(e.ctx), hulk.spaceId));
    const targets = e.ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 1);
  },
};

/** „If you won the combat, you may choose a card in your discard pile and shuffle it into your deck." */
const sensational: CardEffect = {
  after: (e) => {
    const discard = getPlayer(e.ctx.state, e.player).discard;
    if (!e.won || discard.length === 0) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: 'Sensational: smieš zamiešať kartu z odhodu do balíčka',
      optional: true,
      from: 'discard',
      cards: discard.slice(),
      purpose: 'effect',
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    discardToDeck(ctx, choice.player, cardId);
    shuffleDeck(ctx, choice.player);
  },
};

/** „If you dealt 2 or more combat damage, your opponent discards 2 random cards." */
const omegaLevelThreat: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || (e.damage ?? 0) < 2) return;
    discardRandom(e.ctx, opponent, true);
    discardRandom(e.ctx, opponent, true);
  },
};

/** „Draw 2 cards and recover 2 health." */
const doubleJeopardy: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    const hulk = hulkOf(e.ctx, e.player);
    if (hulk) healFighter(e.ctx, hulk.id, 2);
  },
};

/** „Place She-Hulk in a starting space. Draw 1 card. Gain 1 action." */
const jenniferWalters: CardEffect = {
  scheme: (e) => {
    const hulk = hulkOf(e.ctx, e.player);
    if (hulk) {
      const occupied = new Set(e.ctx.state.fighters.map((f) => f.spaceId));
      const spaces = getMap(e.ctx).spaces.filter((s) => s.start !== undefined && !occupied.has(s.id)).map((s) => s.id);
      if (spaces.length > 0) {
        askChoice(e.ctx, {
          kind: 'place',
          player: e.player,
          source: e.cardId,
          label: 'Jennifer Walters, Esq.: polož She-Hulk na štartový priestor',
          optional: false,
          fighterId: hulk.id,
          spaces,
        });
      }
    }
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

/**
 * „Move She-Hulk up to 4 spaces. She may move through opposing fighters.
 * Then, if she is adjacent to an opposing fighter, gain 1 action."
 */
const leapToward: CardEffect = {
  scheme: (e) => {
    const hulk = hulkOf(e.ctx, e.player);
    if (!hulk) return;
    askChoice(e.ctx, {
      kind: 'move',
      player: e.player,
      source: e.cardId,
      label: 'Leap Toward: presuň She-Hulk až o 4 (smie cez súperov)',
      optional: true,
      fighters: [hulk.id],
      max: 4,
      through: true,
    });
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'Leap Toward: akcia, ak stojí pri súperovi',
      optional: false,
      follow: { kind: 'effect', effect: LEAP, data: '' },
    });
  },
  onFollow: (ctx, choice) => {
    const hulk = hulkOf(ctx, choice.player);
    if (!hulk) return;
    const around = new Set(neighbors(getMap(ctx), hulk.spaceId));
    if (ctx.state.fighters.some((f) => around.has(f.spaceId) && areEnemies(ctx.state, f.owner, choice.player))) {
      gainAction(ctx, choice.player);
    }
  },
};

export const sheHulkCards: EffectRegistry = {
  'she_hulk.lady-justice': ladyJustice,
  'she_hulk.the-defense-rests': theDefenseRests,
  'she_hulk.cease-and-desist': ceaseAndDesist,
  [LEGALESE]: legalese,
  'she_hulk.nerve-cluster-strike': nerveClusterStrike,
  [SAVAGE]: savageSheHulk,
  'she_hulk.green-energy': greenEnergy,
  [SENSATIONAL]: sensational,
  'she_hulk.omega-level-threat': omegaLevelThreat,
  'she_hulk.double-jeopardy': doubleJeopardy,
  'she_hulk.jennifer-walters-esq': jenniferWalters,
  [LEAP]: leapToward,
};

export const sheHulkSpecial: HeroSpecial = {
  name: 'Just Throw Something',
  /** „At the start of your turn, you may discard a card to deal damage equal to its BOOST value to a fighter in your zone." */
  startOfTurn: (ctx, player) => {
    if (getPlayer(ctx.state, player).hand.length === 0 || inHerZone(ctx, player).length === 0) return;
    askChoice(ctx, {
      kind: 'confirm',
      player,
      source: null,
      label: 'Just Throw Something: odhodiť kartu a hodiť ju po figúrke vo svojej zóne?',
      optional: true,
      special: HULK,
      yes: 'Odhodiť a hodiť',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, player) => {
    askChoice(ctx, {
      kind: 'card',
      player,
      source: null,
      label: 'Just Throw Something: odhoď kartu — jej BOOST je poškodenie',
      optional: false,
      from: 'hand',
      purpose: 'discard',
      discards: true,
    });
    askChoice(ctx, {
      kind: 'auto',
      player,
      source: null,
      label: 'Just Throw Something: cieľ',
      optional: false,
      follow: { kind: 'special', hero: HULK, data: 'throw' },
    });
  },
  onFollow: (ctx, player) => {
    // Odhodená karta leží navrchu odhodu.
    const thrown = getPlayer(ctx.state, player).discard.at(-1);
    const targets = inHerZone(ctx, player);
    if (!thrown || targets.length === 0) return;
    const amount = cardDef(ctx, thrown).boost;
    if (amount <= 0) return;
    askChoice(ctx, {
      kind: 'damage',
      player,
      source: null,
      label: `Just Throw Something: ${amount} poškodenia figúrke v zóne`,
      optional: false,
      amount,
      targets,
    });
  },
};

